import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from './api';
import { formatDate, taskCode, userDisplayName, displayDateToIso, isoToDisplayDate } from './helpers';

/**
 * Les jalons du projet.
 *
 * Un jalon n'est pas du travail : c'est une date à laquelle un ensemble de
 * tâches doit être terminé. On ne saisit donc jamais son avancement — il se
 * déduit des tâches qu'on lui rattache. Deux contrôles tournent en permanence :
 * un jalon dont la date est passée sans être atteint passe en rouge, et une
 * tâche dont l'échéance dépasse celle de son jalon est signalée, parce qu'elle
 * rend le jalon intenable avant même qu'on ait pris du retard.
 */
function MilestonesPanel({ project, tasks, users, onChanged }) {
  const [jalons, setJalons] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');
  const [message, setMessage] = useState('');
  const [envoi, setEnvoi] = useState(false);

  // Création d'un jalon
  const [creation, setCreation] = useState(false);
  const [nouveau, setNouveau] = useState({ name: '', due_date: '', description: '' });

  // Rattachement en lot
  const [rattachement, setRattachement] = useState(null); // id du jalon visé
  const [selection, setSelection] = useState([]);

  // Modification d'un jalon existant
  const [edition, setEdition] = useState(null); // id du jalon en cours d'édition
  const [brouillon, setBrouillon] = useState({ name: '', due_date: '', description: '' });

  // Tâches d'un jalon : repliées par défaut, visibles au survol de la carte,
  // ou épinglées d'un clic pour rester ouvertes.
  const [epingles, setEpingles] = useState(() => new Set());
  const epingler = (id) =>
    setEpingles((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  // Glisser-déposer pour réordonner
  // Ref pour la logique, état pour l'affichage : voir le même choix dans Gantt.
  const glisseRef = useRef(null);
  const [glisse, setGlisse] = useState(null);
  const [cible, setCible] = useState(null);

  const prendre = (id) => { glisseRef.current = id; setGlisse(id); };
  const relacher = () => { glisseRef.current = null; setGlisse(null); setCible(null); };

  const charger = useCallback(() => {
    setChargement(true);
    api
      .get(`/projects/${project.id}/jalons`)
      .then((res) => setJalons(res.data.jalons || []))
      .catch(() => setErreur('Impossible de charger les jalons.'))
      .finally(() => setChargement(false));
  }, [project.id]);

  useEffect(() => { charger(); }, [charger]);

  const nomDe = (id) => userDisplayName(users.find((u) => u.id === id));

  // Tâches de travail (les jalons eux-mêmes n'apparaissent pas dans la liste
  // de rattachement : un jalon ne se range pas sous un autre).
  const travail = useMemo(() => tasks.filter((t) => !t.est_jalon), [tasks]);

  const creerJalon = (e) => {
    e.preventDefault();
    const nom = nouveau.name.trim();
    if (!nom) return setErreur('Donnez un nom au jalon.');
    const iso = displayDateToIso(nouveau.due_date);
    if (!nouveau.due_date || iso === null) {
      return setErreur('Un jalon doit avoir une date — au format jj/mm/aaaa.');
    }

    setErreur(''); setMessage(''); setEnvoi(true);
    api
      .post('/tasks', {
        project_id: project.id,
        name: nom,
        description: nouveau.description.trim(),
        due_date: iso,
        start_date: iso,
        status: 'Todo',
        priority: 'High',
        est_jalon: true,
      })
      .then(() => {
        setNouveau({ name: '', due_date: '', description: '' });
        setCreation(false);
        setMessage('Jalon créé.');
        charger();
        if (onChanged) onChanged();
      })
      .catch(() => setErreur('La création du jalon a échoué.'))
      .finally(() => setEnvoi(false));
  };

  const ouvrirEdition = (jalon) => {
    setEdition(jalon.id);
    setBrouillon({
      name: jalon.name,
      due_date: isoToDisplayDate(jalon.due_date),
      description: jalon.description || '',
    });
    setRattachement(null);
    setErreur(''); setMessage('');
  };

  const enregistrerEdition = (jalon) => {
    const nom = brouillon.name.trim();
    if (!nom) return setErreur('Le jalon doit garder un nom.');
    const iso = displayDateToIso(brouillon.due_date);
    if (!brouillon.due_date || iso === null) {
      return setErreur('Date invalide — utilisez le format jj/mm/aaaa.');
    }

    setErreur(''); setEnvoi(true);
    api
      .put(`/tasks/${jalon.id}`, {
        name: nom,
        // Un jalon est un point : sa date de début suit sa date visée, sinon
        // il se dessinerait comme une barre sur le Gantt.
        due_date: iso,
        start_date: iso,
        description: brouillon.description.trim(),
      })
      .then(() => {
        const deplacee = iso !== String(jalon.due_date || '').slice(0, 10);
        setMessage(
          deplacee
            ? `Jalon déplacé au ${formatDate(iso)}.`
            : 'Jalon modifié.'
        );
        setEdition(null);
        charger();
        if (onChanged) onChanged();
      })
      .catch(() => setErreur('La modification a échoué.'))
      .finally(() => setEnvoi(false));
  };

  const deposerSur = (surId) => {
    const ids = jalons.map((j) => j.id);
    const depuis = ids.indexOf(glisseRef.current);
    const vers = ids.indexOf(surId);
    relacher();
    if (depuis === -1 || vers === -1 || depuis === vers) return;

    const suivant = [...ids];
    suivant.splice(vers, 0, suivant.splice(depuis, 1)[0]);

    // Affichage immédiat, enregistrement derrière : un déplacement à la souris
    // doit répondre à la vitesse du geste.
    setJalons(suivant.map((id) => jalons.find((j) => j.id === id)));
    api
      .post(`/projects/${project.id}/jalons/ordre`, { ids: suivant })
      .then(() => { charger(); if (onChanged) onChanged(); })
      .catch(() => { setErreur('L’ordre n’a pas pu être enregistré.'); charger(); });
  };

  const supprimerJalon = (jalon) => {
    if (!window.confirm(`Supprimer le jalon « ${jalon.name} » ? Les tâches rattachées seront simplement détachées.`)) return;
    setEnvoi(true);
    api
      .delete(`/tasks/${jalon.id}`)
      .then(() => { setMessage('Jalon supprimé.'); charger(); if (onChanged) onChanged(); })
      .catch(() => setErreur('La suppression a échoué.'))
      .finally(() => setEnvoi(false));
  };

  const ouvrirRattachement = (jalon) => {
    setRattachement(jalon.id);
    setSelection(travail.filter((t) => t.jalon_id === jalon.id).map((t) => t.id));
    setErreur(''); setMessage('');
  };

  const basculer = (id) =>
    setSelection((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const enregistrerRattachement = () => {
    const jalonId = rattachement;
    const avant = travail.filter((t) => t.jalon_id === jalonId).map((t) => t.id);
    const ajoutees = selection.filter((id) => !avant.includes(id));
    const retirees = avant.filter((id) => !selection.includes(id));

    if (ajoutees.length === 0 && retirees.length === 0) {
      setRattachement(null);
      return;
    }

    setEnvoi(true); setErreur('');
    const appels = [];
    if (ajoutees.length) {
      appels.push(api.post(`/projects/${project.id}/jalons/rattacher`, { jalon_id: jalonId, task_ids: ajoutees }));
    }
    if (retirees.length) {
      appels.push(api.post(`/projects/${project.id}/jalons/rattacher`, { jalon_id: null, task_ids: retirees }));
    }

    Promise.all(appels)
      .then(() => {
        setMessage(
          `${ajoutees.length} tâche(s) rattachée(s)` +
            (retirees.length ? `, ${retirees.length} détachée(s)` : '') + '.'
        );
        setRattachement(null);
        charger();
        if (onChanged) onChanged();
      })
      .catch(() => setErreur('Le rattachement a échoué.'))
      .finally(() => setEnvoi(false));
  };

  if (chargement) return <p>Chargement des jalons...</p>;

  const orphelines = travail.filter((t) => !t.jalon_id && t.status !== 'Done');

  return (
    <div className="fade-in">
      {erreur && <div className="error-message" style={{ marginBottom: 12 }}>{erreur}</div>}
      {message && <div className="success-message" style={{ marginBottom: 12 }}>{message}</div>}

      <div className="jalons-intro">
        <strong>Un jalon, c’est une date qui compte.</strong> Fixez la date, rattachez
        les tâches concernées : l’avancement se calcule tout seul et tout le monde est
        prévenu si la date passe. Survolez un jalon pour voir ses tâches ; glissez une
        carte sur une autre pour changer l’ordre.
      </div>

      {!creation ? (
        <button className="btn btn-primary" onClick={() => { setCreation(true); setErreur(''); }}>
          + Nouveau jalon
        </button>
      ) : (
        <form className="jalon-formulaire" onSubmit={creerJalon}>
          <div className="form-row">
            <div className="form-group">
              <label>Nom du jalon</label>
              <input
                value={nouveau.name}
                onChange={(e) => setNouveau({ ...nouveau, name: e.target.value })}
                placeholder="Ex. : Réception du matériel sur site"
                autoFocus
              />
            </div>
            <div className="form-group">
              <label>Date visée</label>
              <input
                value={nouveau.due_date}
                onChange={(e) => setNouveau({ ...nouveau, due_date: e.target.value })}
                placeholder="jj/mm/aaaa"
              />
            </div>
          </div>
          <div className="form-group">
            <label>Ce qui doit être acquis à cette date <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(facultatif)</span></label>
            <textarea
              rows={2}
              value={nouveau.description}
              onChange={(e) => setNouveau({ ...nouveau, description: e.target.value })}
              placeholder="Ex. : matériel livré, contrôlé et stocké au magasin"
            />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="submit" className="btn btn-primary" disabled={envoi}>
              {envoi ? 'Création...' : 'Créer le jalon'}
            </button>
            <button type="button" className="btn" onClick={() => setCreation(false)}>Annuler</button>
          </div>
        </form>
      )}

      {jalons.length === 0 ? (
        <div className="empty-state" style={{ marginTop: 20 }}>
          Aucun jalon pour l’instant. Trois à six jalons suffisent pour un projet :
          au-delà, ils cessent d’être des repères.
        </div>
      ) : (
        <div className="jalons-liste">
          {jalons.map((j) => {
            const etat = j.atteint ? 'atteint' : j.depasse ? 'depasse' : 'ouvert';
            // On ne déplace pas une carte pendant qu'on écrit dedans : le
            // glissement volerait la sélection de texte des champs.
            const glissable = jalons.length > 1 && edition !== j.id && rattachement !== j.id;
            return (
              <div
                key={j.id}
                className={
                  `jalon-carte jalon-${etat}` +
                  (glissable ? ' jalon-carte-glissable' : '') +
                  (epingles.has(j.id) ? ' jalon-carte-epinglee' : '') +
                  (glisse === j.id ? ' jalon-carte-glisse' : '') +
                  (cible === j.id && glisse !== j.id ? ' jalon-carte-cible' : '')
                }
                draggable={glissable}
                onDragStart={(e) => {
                  if (!glissable) return;
                  prendre(j.id);
                  e.dataTransfer.effectAllowed = 'move';
                  try { e.dataTransfer.setData('text/plain', String(j.id)); } catch (err) { /* ignoré */ }
                }}
                onDragOver={(e) => {
                  if (glisseRef.current === null) return;
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                  if (cible !== j.id) setCible(j.id);
                }}
                onDrop={(e) => {
                  if (glisseRef.current === null) return;
                  e.preventDefault();
                  deposerSur(j.id);
                }}
                onDragEnd={relacher}
              >
                <div className="jalon-tete">
                  <div>
                    <div className="jalon-nom">
                      <span className="jalon-losange" />
                      {j.name}
                    </div>
                    <div className="jalon-date">
                      {formatDate(j.due_date)}
                      {j.atteint && <span className="jalon-etiquette jalon-etiquette-ok">Atteint</span>}
                      {j.depasse && (
                        <span className="jalon-etiquette jalon-etiquette-retard">
                          Dépassé de {j.jours_depassement} j
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="jalon-actions">
                    <button className="btn btn-sm" onClick={() => ouvrirEdition(j)}>
                      Modifier
                    </button>
                    <button className="btn btn-sm" onClick={() => ouvrirRattachement(j)}>
                      Rattacher des tâches
                    </button>
                    <button className="btn btn-sm btn-danger" onClick={() => supprimerJalon(j)} disabled={envoi}>
                      Supprimer
                    </button>
                  </div>
                </div>

                {edition === j.id ? (
                  <div className="jalon-edition">
                    <div className="form-row">
                      <div className="form-group">
                        <label>Nom du jalon</label>
                        <input
                          value={brouillon.name}
                          onChange={(e) => setBrouillon({ ...brouillon, name: e.target.value })}
                          autoFocus
                        />
                      </div>
                      <div className="form-group">
                        <label>Date visée</label>
                        <input
                          value={brouillon.due_date}
                          onChange={(e) => setBrouillon({ ...brouillon, due_date: e.target.value })}
                          placeholder="jj/mm/aaaa"
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Ce qui doit être acquis à cette date</label>
                      <textarea
                        rows={2}
                        value={brouillon.description}
                        onChange={(e) => setBrouillon({ ...brouillon, description: e.target.value })}
                      />
                    </div>
                    {j.total > 0 && (
                      <p className="charte-note" style={{ marginBottom: 10 }}>
                        Déplacer la date ne touche pas aux {j.total} tâche{j.total > 1 ? 's' : ''} rattachée
                        {j.total > 1 ? 's' : ''} : leurs échéances restent ce qu’elles sont. Si l’une
                        d’elles finit après la nouvelle date, VIGIE vous le signalera aussitôt.
                      </p>
                    )}
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button className="btn btn-primary btn-sm" onClick={() => enregistrerEdition(j)} disabled={envoi}>
                        {envoi ? 'Enregistrement...' : 'Enregistrer'}
                      </button>
                      <button className="btn btn-sm" onClick={() => setEdition(null)}>Annuler</button>
                    </div>
                  </div>
                ) : (
                  j.description && <p className="jalon-description">{j.description}</p>
                )}

                <div className="jalon-jauge">
                  <div className="progress-bar" style={{ margin: 0 }}>
                    <div className="progress-fill" style={{ width: `${j.avancement}%` }} />
                  </div>
                  {j.total === 0 ? (
                    <span className="jalon-compteur">aucune tâche rattachée</span>
                  ) : (
                    <button
                      type="button"
                      className="jalon-compteur jalon-compteur-bouton"
                      onClick={() => epingler(j.id)}
                      aria-expanded={epingles.has(j.id)}
                      title={epingles.has(j.id) ? 'Replier les tâches' : 'Garder les tâches affichées'}
                    >
                      {j.termine}/{j.total} tâches — {j.avancement}% {epingles.has(j.id) ? '▾' : '▸'}
                    </button>
                  )}
                </div>

                {(j.bloquees > 0 || j.en_retard > 0) && (
                  <div className="jalon-alertes">
                    {j.en_retard > 0 && <span>{j.en_retard} tâche(s) en retard</span>}
                    {j.bloquees > 0 && <span>{j.bloquees} tâche(s) bloquée(s)</span>}
                  </div>
                )}

                {j.incoherentes.length > 0 && (
                  <div className="jalon-incoherence">
                    <strong>Planning incohérent :</strong>{' '}
                    {j.incoherentes.length} tâche{j.incoherentes.length > 1 ? 's' : ''} rattachée
                    {j.incoherentes.length > 1 ? 's' : ''} à ce jalon {j.incoherentes.length > 1 ? 'ont' : 'a'} une
                    échéance postérieure au jalon lui-même — il ne pourra pas être tenu.
                    <ul>
                      {j.incoherentes.map((t) => (
                        <li key={t.task_id}>
                          {taskCode(t.task_id)} — {t.name} (échéance {formatDate(t.due_date)})
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {j.taches.length > 0 && (
                  <table className="jalon-tableau jalon-tableau-repliable">
                    <tbody>
                      {j.taches.map((t) => (
                        <tr key={t.id}>
                          <td style={{ width: 70, color: 'var(--text-muted)' }}>{taskCode(t.id)}</td>
                          <td>{t.name}</td>
                          <td style={{ width: 150 }}>{nomDe(t.assigned_to)}</td>
                          <td style={{ width: 100 }}>{formatDate(t.due_date)}</td>
                          <td style={{ width: 60, textAlign: 'right' }}>{t.progress_percent}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                {rattachement === j.id && (
                  <div className="jalon-rattachement">
                    <div className="jalon-rattachement-tete">
                      Cochez les tâches qui doivent être terminées pour ce jalon
                      <span>{selection.length} sélectionnée(s)</span>
                    </div>
                    <div className="jalon-rattachement-liste">
                      {travail.length === 0 && <p className="charte-note">Ce projet n’a encore aucune tâche.</p>}
                      {travail.map((t) => {
                        const ailleurs = t.jalon_id && t.jalon_id !== j.id;
                        return (
                          <label key={t.id} className="jalon-case">
                            <input
                              type="checkbox"
                              checked={selection.includes(t.id)}
                              onChange={() => basculer(t.id)}
                            />
                            <span className="jalon-case-ref">{taskCode(t.id)}</span>
                            <span className="jalon-case-nom">{t.name}</span>
                            <span className="jalon-case-date">{formatDate(t.due_date)}</span>
                            {ailleurs && (
                              <span className="jalon-case-note">
                                déjà sous « {jalons.find((x) => x.id === t.jalon_id)?.name || 'un autre jalon'} »
                              </span>
                            )}
                          </label>
                        );
                      })}
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                      <button className="btn btn-primary btn-sm" onClick={enregistrerRattachement} disabled={envoi}>
                        {envoi ? 'Enregistrement...' : 'Enregistrer'}
                      </button>
                      <button className="btn btn-sm" onClick={() => setRattachement(null)}>Annuler</button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {jalons.length > 0 && orphelines.length > 0 && (
        <div className="jalon-orphelines">
          <strong>
            {orphelines.length > 1
              ? `${orphelines.length} tâches ouvertes ne sont rattachées à aucun jalon.`
              : '1 tâche ouverte n’est rattachée à aucun jalon.'}
          </strong>{' '}
          Ce n’est pas une faute — toutes les tâches n’ont pas à l’être — mais si
          la liste est longue, c’est souvent le signe qu’il manque un jalon.
        </div>
      )}
    </div>
  );
}

export default MilestonesPanel;
